import * as Phaser from 'phaser';
import {
  ELEVATED_DEPTH_STEP,
  MeshPaintSink,
  elevatedBodiesInPaintOrder,
  elevatedColumnDepth,
  tessellateRing,
  type ElevatedPaintBody,
  type MapDocument,
  type PaintSink,
} from '../sim/map/index.ts';
import { StaticMesh } from './terrain/staticMesh.ts';
import { earcutTriangulate } from './terrain/triangulate.ts';

export type TerrainPaintMode = 'all' | 'fills' | 'chrome';

type Layer = Phaser.GameObjects.Graphics | StaticMesh;

type LayerPair = {
  id: string;
  fill: Layer;
  rim: Layer;
  /** Outline identity + paint key the fill was last built from; `null` forces a rebuild. */
  fillOutline: readonly unknown[] | null;
  fillKey: string | null;
  rimOutline: readonly unknown[] | null;
  rimKey: string | null;
};

export type ColumnPaint = {
  fill: (sink: PaintSink, entry: ElevatedPaintBody) => void;
  stroke: (sink: PaintSink, entry: ElevatedPaintBody) => void;
  /**
   * Everything `fill` depends on apart from the ring outline (sun, look, elevation,
   * colours). When given, a body whose outline and key are unchanged keeps its geometry —
   * an editor drag then rebuilds one body, not the whole map.
   */
  fillKey?: (entry: ElevatedPaintBody) => string;
  /** Same for `stroke` (zoom scale, selection, colours). */
  strokeKey?: (entry: ElevatedPaintBody) => string;
};

export type ElevatedBodyPoolOptions = {
  /**
   * Retain column fills and rims as static GPU meshes instead of Graphics command buffers
   * replayed every frame (docs/PERFORMANCE.md). Requires WebGL; falls back to Graphics.
   */
  staticGeometry?: boolean;
};

/**
 * One fill + one rim object per island/rock so a zoom restroke can rebuild the
 * rim without wiping the cliffs. Phaser depth is per object — a single land
 * layer cannot hide a palm behind a higher terrace, and a shared rim layer
 * would paint a short island's outline over a taller cliff.
 *
 * Rim sits a fraction of a depth step above its fill, still below the next
 * body's fill and the prop slot.
 *
 * With `staticGeometry` both objects are `StaticMesh`: Graphics re-tessellates and
 * re-uploads every command each frame, which was 80–90% of a play frame on an
 * archipelago. Fills are rebuilt on map / sun / look changes, rims on zoom settle.
 */
export class ElevatedBodyGraphicsPool {
  private readonly layers = new Map<string, LayerPair>();
  private readonly scene: Phaser.Scene;
  private readonly baseDepth: number;
  private readonly staticGeometry: boolean;

  constructor(scene: Phaser.Scene, baseDepth: number, options: ElevatedBodyPoolOptions = {}) {
    this.scene = scene;
    this.baseDepth = baseDepth;
    this.staticGeometry = options.staticGeometry === true && StaticMesh.isSupported(scene);
  }

  /** True when fills and rims are retained meshes rather than per-frame Graphics. */
  get usesStaticGeometry(): boolean {
    return this.staticGeometry;
  }

  /** Triangles currently uploaded across every body (Perf readout / tests). */
  get staticTriangleCount(): number {
    let total = 0;

    for (const pair of this.layers.values()) {
      if (pair.fill instanceof StaticMesh) {
        total += pair.fill.triangleCount;
      }

      if (pair.rim instanceof StaticMesh) {
        total += pair.rim.triangleCount;
      }
    }

    return total;
  }

  sync(
    map: MapDocument,
    paint: ColumnPaint,
    mode: TerrainPaintMode = 'all',
  ): void {
    const bodies = elevatedBodiesInPaintOrder(map);
    const seen = new Set<string>();

    for (let index = 0; index < bodies.length; index += 1) {
      const entry = bodies[index];

      if (!entry) {
        continue;
      }

      const key = `${entry.kind}:${entry.body.id}`;
      seen.add(key);
      const existing = this.layers.get(key);
      const created = !existing || !existing.fill.scene || !existing.rim.scene;
      const pair: LayerPair = created
        ? {
            id: entry.body.id,
            fill: this.createLayer(),
            rim: this.createLayer(),
            fillOutline: null,
            fillKey: null,
            rimOutline: null,
            rimKey: null,
          }
        : existing;

      if (created) {
        this.layers.set(key, pair);
      }

      const depth = elevatedColumnDepth(this.baseDepth, index);
      pair.fill.setDepth(depth);
      pair.rim.setDepth(depth + ELEVATED_DEPTH_STEP * 0.25);
      // `tessellateRing` returns the same array while the ring is unchanged, so its
      // identity is a free change detector for the outline.
      const outline = tessellateRing(entry.body);

      if (mode !== 'chrome' || created) {
        const fillKey = paint.fillKey?.(entry) ?? null;

        if (
          created ||
          fillKey === null ||
          pair.fillOutline !== outline ||
          pair.fillKey !== fillKey
        ) {
          repaint(pair.fill, (sink) => paint.fill(sink, entry));
          pair.fillOutline = outline;
          pair.fillKey = fillKey;
        }
      }

      if (mode !== 'fills' || created) {
        const rimKey = paint.strokeKey?.(entry) ?? null;

        if (
          created ||
          rimKey === null ||
          pair.rimOutline !== outline ||
          pair.rimKey !== rimKey
        ) {
          repaint(pair.rim, (sink) => paint.stroke(sink, entry));
          pair.rimOutline = outline;
          pair.rimKey = rimKey;
        }
      }
    }

    for (const [key, pair] of this.layers) {
      if (seen.has(key)) {
        continue;
      }

      pair.fill.destroy();
      pair.rim.destroy();
      this.layers.delete(key);
    }
  }

  /**
   * Hide columns whose body id is not in `visibleIds`. Does not clear or
   * rebuild the uploaded triangles.
   */
  applyVisibility(visibleIds: ReadonlySet<string>): void {
    for (const pair of this.layers.values()) {
      const on = visibleIds.has(pair.id);

      if (pair.fill.visible !== on) {
        pair.fill.setVisible(on);
      }

      if (pair.rim.visible !== on) {
        pair.rim.setVisible(on);
      }
    }
  }

  destroy(): void {
    for (const pair of this.layers.values()) {
      pair.fill.destroy();
      pair.rim.destroy();
    }

    this.layers.clear();
  }

  private createLayer(): Layer {
    if (this.staticGeometry) {
      return this.scene.add.existing(new StaticMesh(this.scene));
    }

    return this.scene.add.graphics();
  }
}

function repaint(layer: Layer, paint: (sink: PaintSink) => void): void {
  if (layer instanceof StaticMesh) {
    const sink = new MeshPaintSink(earcutTriangulate);
    paint(sink);
    layer.setGeometry(sink.finish());
    return;
  }

  layer.clear();
  paint(layer);
}
